import { type IServer, type SelectOption } from '@interface/ICommon';
import { setCookie } from '@utils/common';
import { SERVERID_COOKIE_KEY } from '@utils/constants';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Select, { components, type DropdownIndicatorProps, type StylesConfig } from 'react-select';

const SeverSelect = ({ serverList, activeServerId }: { serverList: IServer[] | undefined; activeServerId: string | null }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [svrList, setSvrList] = useState<SelectOption[]>([]);

    useEffect(() => {
        if (!serverList) return;
        const templist = serverList.map((svr) => ({
            value: svr.serverId.toString(),
            label: svr.serverName,
        }));

        setSvrList(templist);
    }, [activeServerId, serverList]);

    const onChange = (value: string) => {
        searchParams.set('server', value);
        setSearchParams(searchParams);
        setCookie(SERVERID_COOKIE_KEY, value);
    };

    if (!svrList) return null;
    return (
        <div className="w-[calc(100%-48px)] lg:w-[auto]">
            <Select
                options={svrList}
                value={svrList?.filter((option) => option.value === activeServerId?.toString())}
                onChange={(option) => onChange((option as SelectOption).value)}
                isSearchable={false}
                styles={customStyles}
                components={{ DropdownIndicator }}
                classNamePrefix="select-styles"
            />
        </div>
    );
};

export default SeverSelect;

const DropdownIndicator = (props: DropdownIndicatorProps) => {
    return (
        components.DropdownIndicator && (
            <components.DropdownIndicator {...props}>
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="10" viewBox="0 0 8 10" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M8 6L4 10L0 6H8ZM4 0L8 4H0L4 0Z" fill="black" fillOpacity="0.45" />
                </svg>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12 9L8 13L4 9H12ZM8 3L12 7H4L8 3Z" fill="white" fillOpacity="0.38" />
                </svg>
            </components.DropdownIndicator>
        )
    );
};

const customStyles: StylesConfig = {
    option: (baseStyles, state) => ({
        ...baseStyles,
        paddingTop: '9px',
        paddingBottom: '12px',
        color: state.isDisabled ? 'var(--rk-select-disabled)' : state.isSelected ? 'var(--ncui-black093)' : 'var(--ncui-black093)',
        backgroundColor: state.isDisabled ? 'var(--ncui-gray004-hex)' : state.isSelected ? 'var(--ncui-bg)' : 'var(--ncui-gray004-hex)',
        borderBottom: '1px solid var(--ncui-gray012)',
        borderLeft: '1px solid var(--ncui-gray012)',
        borderRight: '1px solid var(--ncui-gray012)',
        cursor: state.isDisabled ? 'default' : 'pointer',
        '&:hover': {
            color: state.isDisabled ? 'var(--rk-select-disabled)' : state.isSelected ? 'var(--ncui-black093)' : 'var(--ncui-point-text)',
        },
    }),
    control: (baseStyles) => ({
        ...baseStyles,
        height: '42px',
        color: 'var(--ncui-black093)',
        backgroundColor: 'var(--ncui-bg)',
        borderColor: 'var(--ncui-gray012)',
        boxShadow: 'none',
        minWidth: '140px',
        fontSize: '15px',
        '&:hover': {
            borderColor: 'var(--ncui-gray012)',
        },
    }),
    menu: (baseStyles) => ({
        ...baseStyles,
        marginTop: 0,
        borderRadius: 'none',
        boxShadow: 'none',
        borderBottom: '1px solid var(--ncui-gray012-hex)',
    }),
    menuList: (baseStyles) => ({
        ...baseStyles,
        padding: 0,
        fontSize: '15px',
    }),
    singleValue: (baseStyles) => ({
        ...baseStyles,
        color: 'var(--ncui-black093)',
        backgroundColor: 'var(--ncui-bg)',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
};
